import { useState } from 'react';

function ExperimentTracker({ experiments, onSelectExperiment, onCompareExperiments }) {
    const [selectedExperiments, setSelectedExperiments] = useState([]);
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');

    const toggleSelection = (expId) => {
        setSelectedExperiments(prev =>
            prev.includes(expId)
                ? prev.filter(id => id !== expId)
                : [...prev, expId]
        );
    };

    const sortedExperiments = [...experiments].sort((a, b) => {
        let aVal = a[sortBy];
        let bVal = b[sortBy];

        // Handle nested metrics
        if (sortBy.startsWith('metrics.')) {
            const metric = sortBy.split('.')[1];
            aVal = a.metrics[metric];
            bVal = b.metrics[metric];
        }

        if (sortOrder === 'asc') {
            return aVal > bVal ? 1 : -1;
        } else {
            return aVal < bVal ? 1 : -1;
        }
    });

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('desc');
        }
    };

    return (
        <div className="flex-1 overflow-auto bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-1">
                                🔬 Experiment Tracker
                            </h2>
                            <p className="text-gray-600">
                                Track and compare your ML experiments
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => onCompareExperiments(selectedExperiments)}
                                disabled={selectedExperiments.length < 2}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                Compare Selected ({selectedExperiments.length})
                            </button>
                            <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium flex items-center gap-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                New Experiment
                            </button>
                        </div>
                    </div>

                    {/* Summary Stats */}
                    <div className="grid grid-cols-4 gap-4">
                        <StatCard
                            label="Total Experiments"
                            value={experiments.length}
                            color="blue"
                        />
                        <StatCard
                            label="Running"
                            value={experiments.filter(e => e.status === 'running').length}
                            color="orange"
                        />
                        <StatCard
                            label="Completed"
                            value={experiments.filter(e => e.status === 'complete').length}
                            color="green"
                        />
                        <StatCard
                            label="Best Accuracy"
                            value={Math.max(...experiments.map(e => e.metrics.accuracy)).toFixed(3)}
                            color="purple"
                        />
                    </div>
                </div>

                {/* Experiments Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-4 py-3 text-left">
                                        <input
                                            type="checkbox"
                                            className="rounded border-gray-300"
                                            checked={selectedExperiments.length === experiments.length}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedExperiments(experiments.map(exp => exp.id));
                                                } else {
                                                    setSelectedExperiments([]);
                                                }
                                            }}
                                        />
                                    </th>
                                    <SortableHeader
                                        label="Name"
                                        column="name"
                                        sortBy={sortBy}
                                        sortOrder={sortOrder}
                                        onSort={handleSort}
                                    />
                                    <SortableHeader
                                        label="Model"
                                        column="model"
                                        sortBy={sortBy}
                                        sortOrder={sortOrder}
                                        onSort={handleSort}
                                    />
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                                        Parameters
                                    </th>
                                    <SortableHeader
                                        label="Accuracy"
                                        column="metrics.accuracy"
                                        sortBy={sortBy}
                                        sortOrder={sortOrder}
                                        onSort={handleSort}
                                    />
                                    <SortableHeader
                                        label="F1 Score"
                                        column="metrics.f1"
                                        sortBy={sortBy}
                                        sortOrder={sortOrder}
                                        onSort={handleSort}
                                    />
                                    <SortableHeader
                                        label="Duration"
                                        column="duration"
                                        sortBy={sortBy}
                                        sortOrder={sortOrder}
                                        onSort={handleSort}
                                    />
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                                        Status
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {sortedExperiments.map(exp => (
                                    <tr
                                        key={exp.id}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => onSelectExperiment(exp)}
                                    >
                                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                className="rounded border-gray-300"
                                                checked={selectedExperiments.includes(exp.id)}
                                                onChange={() => toggleSelection(exp.id)}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-gray-900">{exp.name}</span>
                                                {exp.is_best && (
                                                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                                                        ⭐ Best
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700">
                                            {exp.model}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-xs text-gray-600 font-mono">
                                                {Object.entries(exp.params)
                                                    .map(([k, v]) => `${k}:${v}`)
                                                    .join(', ')}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                            {(exp.metrics.accuracy * 100).toFixed(1)}%
                                        </td>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                            {exp.metrics.f1.toFixed(3)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {exp.duration}
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusBadge status={exp.status} />
                                        </td>
                                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex gap-1">
                                                <button
                                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                                    title="View"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    className="p-1 text-green-600 hover:bg-green-50 rounded"
                                                    title="Deploy"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                    </svg>
                                                </button>
                                                {exp.status === 'running' && (
                                                    <button
                                                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                        title="Stop"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* AI Analysis */}
                {experiments.length > 0 && (
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border-2 border-purple-200 p-6">
                        <div className="flex items-start gap-4">
                            <div className="text-4xl">📊</div>
                            <div className="flex-1">
                                <h3 className="font-bold text-gray-900 mb-2">Evaluator's Analysis</h3>
                                <div className="text-sm text-gray-700 space-y-2">
                                    <p>
                                        <strong>Winner:</strong> {experiments.find(e => e.is_best)?.name} ({experiments.find(e => e.is_best)?.model})
                                    </p>
                                    <ul className="list-disc list-inside space-y-1 text-gray-600">
                                        <li>Best accuracy/speed tradeoff for production deployment</li>
                                        <li>Consistent performance across validation sets</li>
                                        <li>Consider deploying exp_002 for maximum accuracy</li>
                                    </ul>
                                </div>
                                <button className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                                    View detailed analysis
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function StatCard({ label, value, color }) {
    const colorClasses = {
        blue: 'bg-blue-50 border-blue-200 text-blue-700',
        orange: 'bg-orange-50 border-orange-200 text-orange-700',
        green: 'bg-green-50 border-green-200 text-green-700',
        purple: 'bg-purple-50 border-purple-200 text-purple-700'
    };

    return (
        <div className={`rounded-lg p-4 border ${colorClasses[color]}`}>
            <div className="text-sm font-medium mb-1">{label}</div>
            <div className="text-2xl font-bold">{value}</div>
        </div>
    );
}

function StatusBadge({ status }) {
    const statusConfig = {
        running: {
            bg: 'bg-orange-100',
            text: 'text-orange-700',
            label: 'Running',
            icon: '🔄'
        },
        complete: {
            bg: 'bg-green-100',
            text: 'text-green-700',
            label: 'Complete',
            icon: '✅'
        },
        failed: {
            bg: 'bg-red-100',
            text: 'text-red-700',
            label: 'Failed',
            icon: '❌'
        }
    };

    const config = statusConfig[status] || statusConfig.complete;

    return (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
            <span>{config.icon}</span>
            {config.label}
        </span>
    );
}

function SortableHeader({ label, column, sortBy, sortOrder, onSort }) {
    const isActive = sortBy === column;

    return (
        <th
            className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100"
            onClick={() => onSort(column)}
        >
            <div className="flex items-center gap-1">
                {label}
                {isActive && (
                    <svg
                        className={`w-4 h-4 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                )}
            </div>
        </th>
    );
}

export default ExperimentTracker;
